import {
  parseVariableDefinitions,
  resolveMustacheString,
  type AvatarBlock,
} from '@blogforge/shared';
import { canvasBlockStyle } from '../../helpers';
import { ImageUploader } from '../ImageUploader';
import { useEditorStore } from '../../stores/editorStore';
import type { DragPath } from '../../types';

export function AvatarRenderer({
  block,
  path,
  rawDefinitions,
}: {
  block: AvatarBlock;
  path: DragPath;
  rawDefinitions: unknown;
}) {
  const definitions = rawDefinitions ? parseVariableDefinitions(rawDefinitions) : null;

  const context: Record<string, any> = {};

  if (definitions) {
    for (const [name, value] of Object.entries(definitions)) {
      if (value.default) {
        context[name] = value.default;
      }
    }
  }

  const updateBlock = useEditorStore((s) => s.updateBlock);
  const { image, radius } = block.attrs;
  const size = block.attrs.size ?? image.width ?? 64;
  // Same rule as the renderers: no picture size means fill the frame.
  const fills = block.attrs.size === undefined || image.width === undefined;
  const imgW = fills ? size : Math.min(image.width!, size);
  const imgH = fills ? size : Math.min(image.height ?? imgW, size);
  const resolvedSrc = image.srcIsMustache
    ? resolveMustacheString(image.src, context, { escape: false })
    : image.src;

  // Split like the email renderer: the frame (background, border, shadow,
  // radius) around the picture, alignment and spacing on the row. attrs.radius
  // is the shape, so the style's own radii are dropped.
  const {
    backgroundColor,
    border,
    borderColor,
    boxShadow,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomLeftRadius,
    borderBottomRightRadius,
    textAlign,
    ...wrapperStyle
  } = canvasBlockStyle(block.style);
  const align = textAlign && textAlign !== 'justify' ? textAlign : 'center';

  return (
    <div style={{ ...wrapperStyle, textAlign: align }}>
      {resolvedSrc ? (
        <span
          className="inline-flex items-center justify-center overflow-hidden align-middle"
          // The canvas shows the link but never follows it: a click selects.
          title={block.attrs.href ? `Links to ${block.attrs.href}` : undefined}
          style={{
            width: size,
            height: size,
            borderRadius: radius,
            backgroundColor,
            border,
            borderColor,
            boxShadow,
          }}
        >
          <img
            src={resolvedSrc}
            alt={image.alt || ''}
            width={imgW}
            height={imgH}
            className="block"
            style={{ width: imgW, height: imgH, objectFit: 'cover' }}
          />
        </span>
      ) : (
        <ImageUploader
          onSuccess={(url) =>
            updateBlock(
              block.id,
              { attrs: { ...block.attrs, image: { ...image, src: url, srcIsMustache: false } } },
              path,
            )
          }
          variables={{
            skipOptimize: image.skipOptimize ? '1' : '0',
          }}
        />
      )}
    </div>
  );
}
